import { useMemo, useState } from 'react';
import { Button, Flex, Typography } from 'antd';
import dayjs from 'dayjs';

import useModalControls from 'hooks/useModalControls';

import PrecoEditPaymentModal from './PrecoEditPaymentModal';
import PrecoScheduleTable from './PrecoScheduleTable';
import {
  getPrecoBalanceAlert,
  getPrecoScheduleTotal,
} from './precoPaymentUtils';
import {
  PRECO_STEPS,
  setNextStep,
  setPrecoSchedule,
  usePrecoState,
} from './precoState';

const { Title } = Typography;

const PrecoReviewScheduleStep = ({
  onContinue = () => setNextStep(PRECO_STEPS.DISCLOSURE),
}) => {
  const { offer, schedule } = usePrecoState();
  const editPaymentModal = useModalControls();

  const [editPaymentFormInitialValues, setEditPaymentFormInitialValues] =
    useState({});
  const [updatePaymentType, setUpdatePaymentType] = useState('');

  const onAddPayment = () => {
    setUpdatePaymentType('add');
    editPaymentModal.show();
  };

  const onEditPayment = (record) => {
    setEditPaymentFormInitialValues({
      amount: Number((record.amount / 100).toFixed(2)),
      date: dayjs(record.date),
    });
    setUpdatePaymentType('edit');
    editPaymentModal.show();
  };

  const tableData = useMemo(
    () =>
      schedule
        .map((payment, index) => ({
          ...payment,
          key: `payment-${index}`,
          editable: true,
        }))
        .sort((a, b) => dayjs(a.date) - dayjs(b.date)),
    [schedule],
  );

  const hasEdits = schedule.some((payment) => payment.changeType);

  const totalPayments = useMemo(
    () => getPrecoScheduleTotal(schedule),
    [schedule],
  );

  // An enrolled offer may already have payments, so the new schedule only has to cover
  // what is left; a not yet enrolled offer has nothing paid, so that is the full settlement.
  const balanceToCover = offer.remainingBalance ?? offer.settlementBalance;

  const balanceAlert = useMemo(
    () =>
      getPrecoBalanceAlert({
        totalPayments,
        balance: balanceToCover,
        outstandingBalance: offer.originalOutstandingBalance,
      }),
    [totalPayments, balanceToCover, offer.originalOutstandingBalance],
  );

  return (
    <div>
      <Title level={5}>Review Schedule</Title>
      <PrecoScheduleTable
        data={tableData}
        schedule={schedule}
        setSchedule={setPrecoSchedule}
        onEditPayment={onEditPayment}
        balanceAlert={balanceAlert}
        showStatus={hasEdits}
      />
      <Flex gap="var(--spacing-sm)" justify="right">
        <Button onClick={onAddPayment}>Add a Payment</Button>
        <Button
          type="primary"
          disabled={balanceAlert?.type === 'error'}
          onClick={onContinue}
        >
          Continue
        </Button>
      </Flex>
      <PrecoEditPaymentModal
        {...editPaymentModal}
        transactions={tableData}
        schedule={schedule}
        setSchedule={setPrecoSchedule}
        expirationDate={offer.expirationDate}
        initialValues={editPaymentFormInitialValues}
        setInitialValues={setEditPaymentFormInitialValues}
        type={updatePaymentType}
      />
    </div>
  );
};

export default PrecoReviewScheduleStep;
